/**
 * fuzzySearch.ts - F023 Fase 3.1
 * 
 * Búsqueda fuzzy optimizada usando uFuzzy.
 * Reemplaza Fuse.js con 10-100x mejor rendimiento.
 * 
 * CARACTERÍSTICAS:
 * - uFuzzy para matching ultra-rápido
 * - Normalización de texto integrada
 * - API compatible con uso previo de Fuse.js
 * - Ordenación por relevancia
 * 
 * @version 1.0.0
 * @date 2025-12-04
 * @see F023 Fase 3 - Optimizaciones
 */

import uFuzzy from '@leeoniya/ufuzzy';

// ============================================================================
// CONFIGURACIÓN
// ============================================================================

/** Opciones uFuzzy optimizadas para español */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  // Modo: 0 = intraMode (caracteres individuales), 1 = interMode (palabras)
  intraMode: 1,
  // Permitir separación entre términos de búsqueda (string, no RegExp)
  interSplit: '[\\s.,\\-\\/]+',
  // Distancia máxima entre caracteres en match
  intraChars: '[a-záéíóúüñ\\d]',
  // Límite de resultados internos antes de ordenar
  intraIns: 1,
};

/**
 * CVLARGO: por encima de este número de palabras la aguja se busca sin variantes por errata.
 *
 * Con `intraMode: 1` cada palabra se convierte en unas 30 variantes (sustitución, trasposición,
 * omisión, inserción) encadenadas por `.*?`; con 5 o más palabras y una lista de nombres que
 * empiezan igual, la expresión retrocede de forma catastrófica y bloquea el hilo de la página.
 * Medido el 29-oct-2026 con los nombres de DERA de hoy («Colegio de Educación Infantil y
 * Primaria …»): 307 de 2 132 búsquedas de educación de más de 1 s (máx. 9,4 s; en Node, minutos)
 * y 122 de energía (San Roque); con la guarda en 4, máx. 26 y 45 ms y el mismo resultado.
 */
export const MAX_PALABRAS_CON_ERRATA = 4;

/** CVLARGO: palabras exactas y en orden, sin variantes por errata (no retrocede). */
const UFUZZY_OPTIONS_SIN_ERRATA: uFuzzy.Options = {
  ...UFUZZY_OPTIONS,
  intraMode: 0,
  intraIns: 0,
};

/** CVLARGO: elige el buscador según el número de palabras de la aguja ya normalizada. */
function buscadorPara(normalizedQuery: string, conErrata: uFuzzy, sinErrata: uFuzzy): uFuzzy {
  return normalizedQuery.split(' ').length > MAX_PALABRAS_CON_ERRATA ? sinErrata : conErrata;
}

/**
 * UFDIRECT: buscador de uFuzzy con la guarda de agujas largas, para los servicios que crean
 * su propia instancia en vez de pasar por `FastFuzzy`.
 *
 * Solo expone `search`: es lo único que usan los servicios que la llevan; así ningún método
 * sin guarda (`filter`, `info`, `sort`) queda a mano.
 */
export type UFuzzyConGuarda = Pick<uFuzzy, 'search'>;

/**
 * UFDIRECT: crea un buscador con las opciones del servicio y la misma guarda que `FastFuzzy`.
 *
 * Por encima de `MAX_PALABRAS_CON_ERRATA` términos busca con una segunda instancia sin
 * variantes por errata (`intraMode 0` e `intraIns/Sub/Trn/Del 0`), conservando el hueco entre
 * palabras (`interIns`), los límites (`interLft`/`interRgt`) y el resto de opciones. Los
 * términos se cuentan con `split` de la propia instancia, no por espacios: «c.e.i.p. torremar»
 * son 2 palabras y 5 términos, y lo que retrocede son los términos.
 *
 * Medido el 30-oct-2026 con nombres y direcciones reales (`.ptel/p0_ufdirect_20261030/
 * AUDIT_UFDIRECT.md`): con `intraMode 1` y hueco 6, `CDAUR2Service` se atasca desde 6 palabras
 * (201 de 7 500 búsquedas de más de 10 s) y `EduCentersR2Service` desde 7 (más de 20 s); sin
 * variantes por errata, 27 202 búsquedas, máx. 23 ms. Con 3-4 términos y erratas, 31 084
 * búsquedas, ninguna de más de 1 s. Calibrado: colegios 6 202 → 6 203 aciertos; en CDAU solo
 * cambia un acierto falso.
 */
export function crearUFuzzyConGuarda(opciones: uFuzzy.Options): UFuzzyConGuarda {
  const conErrata = new uFuzzy(opciones);
  const sinErrata = new uFuzzy({
    ...opciones,
    intraMode: 0,
    intraIns: 0,
    intraSub: 0,
    intraTrn: 0,
    intraDel: 0,
  });
  return {
    search: (haystack, needle, outOfOrder, infoThresh, preFiltered) => {
      const uf = conErrata.split(needle).length > MAX_PALABRAS_CON_ERRATA ? sinErrata : conErrata;
      return uf.search(haystack, needle, outOfOrder, infoThresh, preFiltered);
    },
  };
}

// ============================================================================
// TIPOS
// ============================================================================

export interface FuzzySearchOptions<T> {
  /** Campo(s) donde buscar */
  keys: (keyof T | { name: keyof T; weight?: number })[];
  /** Umbral de coincidencia (0-1, menor = más estricto) */
  threshold?: number;
  /** Límite de resultados */
  limit?: number;
}

export interface FuzzyResult<T> {
  item: T;
  score: number;
  matches?: { key: string; indices: [number, number][] }[];
}

/** NOTAPOS: resultado de `FastFuzzy.buscarPorParecido`. */
export interface ResultadoParecido<T> extends FuzzyResult<T> {
  /** Nota de parecido 0-100 (`score` = 1 − nota/100, 0 = mejor, como en `search`). */
  nota: number;
  /** Solo en el 1.º: el 2.º casa con nota a ≤ `MARGEN_EMPATE` (homónimos); su nota no pasa de `NOTA_TOPE_EMPATE`. */
  empate?: boolean;
}

/** NOTAPOS (`DOP-4` = a): diferencia de nota entre el 1.º y el 2.º por debajo de la cual son un empate. */
export const MARGEN_EMPATE = 2;

/** NOTAPOS (`DOP-4` = a): nota máxima en empate; pasa el corte de la oleada 1 (70) sin confirmarse sola (85). */
export const NOTA_TOPE_EMPATE = 84;

/**
 * NOTAPOS (`DOP-1` = a): nota de parecido 0-100 desde la `Info` de uFuzzy de la misma búsqueda.
 * Mitad, palabras de la aguja que casan exactas (`terms`); mitad, letras de la aguja cubiertas (`chars`).
 *
 * `DOP-1b` = a: las letras sueltas de las siglas («e i bocoy», «c e i p arco iris») no cuentan como
 * palabras; si no, «E.I. Bocoy» sacaba 67, la búsqueda contaba como hallada sin pasar el 70 de la
 * cascada y no se lanzaba el reintento de `PREFEDU` (64 filas peores en el simulado).
 */
export function notaParecido(agujaNormalizada: string, terms: number, chars: number): number {
  const todas = agujaNormalizada.split(' ').filter(Boolean);
  const palabras = todas.filter(p => p.length >= 2).length || todas.length;
  const letras = agujaNormalizada.replace(/ /g, '').length;
  if (!palabras || !letras) return 0;
  return Math.round(100 * (0.5 * Math.min(1, terms / palabras) + 0.5 * Math.min(1, chars / letras)));
}

// ============================================================================
// NORMALIZACIÓN
// ============================================================================

/**
 * Normaliza texto para búsqueda (elimina acentos, minúsculas, espacios extra)
 */
export function normalizeForSearch(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')  // Eliminar acentos
    .replace(/[^a-z0-9\s]/g, ' ')     // Solo alfanuméricos
    .replace(/\s+/g, ' ')
    .trim();
}

// ============================================================================
// CLASE PRINCIPAL
// ============================================================================

/**
 * Buscador fuzzy optimizado con uFuzzy
 */
export class FastFuzzy<T extends Record<string, unknown>> {
  private uf: uFuzzy;
  private ufSinErrata: uFuzzy;
  private items: T[];
  private haystack: string[];
  private keys: { name: keyof T; weight: number }[];
  private threshold: number;
  
  constructor(items: T[], options: FuzzySearchOptions<T>) {
    this.items = items;
    this.threshold = options.threshold ?? 0.4;
    
    // Normalizar keys a formato uniforme
    this.keys = options.keys.map(k => {
      if (typeof k === 'string' || typeof k === 'number' || typeof k === 'symbol') {
        return { name: k, weight: 1 };
      }
      return { name: k.name, weight: k.weight ?? 1 };
    });
    
    // Crear haystack combinando campos relevantes
    this.haystack = items.map(item => {
      const parts: string[] = [];
      for (const key of this.keys) {
        const value = item[key.name];
        if (typeof value === 'string' && value) {
          parts.push(normalizeForSearch(value));
        }
      }
      return parts.join(' ');
    });
    
    // Crear instancia uFuzzy
    this.uf = new uFuzzy(UFUZZY_OPTIONS);
    this.ufSinErrata = new uFuzzy(UFUZZY_OPTIONS_SIN_ERRATA);
  }
  
  /**
   * Busca items que coincidan con la query
   */
  search(query: string, options?: { limit?: number }): FuzzyResult<T>[] {
    if (!query || query.trim().length === 0) {
      return [];
    }
    
    const normalizedQuery = normalizeForSearch(query);
    if (normalizedQuery.length < 2) {
      return [];
    }
    
    const limit = options?.limit ?? 10;
    
    // Búsqueda con uFuzzy (CVLARGO: sin variantes por errata si la aguja es larga)
    const uf = buscadorPara(normalizedQuery, this.uf, this.ufSinErrata);
    const [idxs, info, order] = uf.search(this.haystack, normalizedQuery);
    
    if (!idxs || idxs.length === 0) {
      return [];
    }
    
    // Procesar resultados
    const results: FuzzyResult<T>[] = [];
    const indices = order || idxs;
    
    for (let i = 0; i < Math.min(indices.length, limit); i++) {
      const idx = order ? idxs[order[i]] : indices[i];
      
      // Calcular score basado en info
      let score: number;
      if (info && order) {
        const infoIdx = order[i];
        // uFuzzy: menor score = mejor match
        // Normalizamos a 0-1 donde 0 = mejor
        const rawScore = info.idx?.[infoIdx] ?? 0;
        score = Math.min(1, rawScore / 100);
      } else {
        // Sin info detallada, usar posición como proxy
        score = i / indices.length * 0.5;
      }
      
      // Filtrar por threshold
      if (score > this.threshold) continue;
      
      results.push({
        item: this.items[idx],
        score,
      });
    }
    
    return results;
  }

  /**
   * NOTAPOS 30-oct-2026 · búsqueda con nota de parecido (`DOP-NOTAPOS-1` a `-4` = a).
   *
   * `search` puntúa con `info.idx`, que en uFuzzy es el índice en el pajar: la nota es el puesto
   * en la lista y el `threshold` corta en ese puesto (0,35 → nada más allá del 35, aunque el
   * nombre coincida). Aquí, en cambio:
   * - nota = `notaParecido` con `terms` y `chars` de la `Info` de la misma búsqueda (sin búsqueda extra);
   * - se descarta por nota (`1 − nota/100 > threshold`), no por puesto; el orden es el de uFuzzy;
   * - si el 2.º casa con nota a ≤ `MARGEN_EMPATE` del 1.º (homónimos), el 1.º no pasa de `NOTA_TOPE_EMPATE`.
   *
   * Medido en el simulado de municipios grandes (`.ptel/p0_notapos_20261030/`): con nota ≥ 85 y
   * sin empate aciertan 308 de 309; en empate, 32 de 59.
   */
  buscarPorParecido(query: string, options?: { limit?: number }): ResultadoParecido<T>[] {
    if (!query || query.trim().length === 0) return [];
    const normalizedQuery = normalizeForSearch(query);
    if (normalizedQuery.length < 2) return [];
    const limit = options?.limit ?? 10;

    const uf = buscadorPara(normalizedQuery, this.uf, this.ufSinErrata);
    // Sin `Info` no hay nota de parecido, y uFuzzy la omite por encima de 1 000 coincidencias: se pide siempre.
    const [idxs, info, order] = uf.search(this.haystack, normalizedQuery, 0, Infinity);
    // Sin coincidencias uFuzzy devuelve `[[], {}, []]`.
    if (!idxs || idxs.length === 0 || !info || !order || !Array.isArray(info.idx)) return [];

    const candidatos = order.map(o => ({ idx: info.idx[o], nota: notaParecido(normalizedQuery, info.terms[o], info.chars[o]) }));
    const empate = candidatos.length > 1 && Math.abs(candidatos[0].nota - candidatos[1].nota) <= MARGEN_EMPATE;

    const results: ResultadoParecido<T>[] = [];
    for (let i = 0; i < candidatos.length && results.length < limit; i++) {
      const nota = i === 0 && empate ? Math.min(candidatos[i].nota, NOTA_TOPE_EMPATE) : candidatos[i].nota;
      const score = 1 - nota / 100;
      if (score > this.threshold) continue;
      results.push({ item: this.items[candidatos[i].idx], score, nota, ...(i === 0 && empate ? { empate: true } : {}) });
    }
    return results;
  }
  
  /**
   * Actualiza el conjunto de items
   */
  setItems(items: T[]): void {
    this.items = items;
    this.haystack = items.map(item => {
      const parts: string[] = [];
      for (const key of this.keys) {
        const value = item[key.name];
        if (typeof value === 'string' && value) {
          parts.push(normalizeForSearch(value));
        }
      }
      return parts.join(' ');
    });
  }
}

// ============================================================================
// FUNCIONES DE UTILIDAD
// ============================================================================

/**
 * Búsqueda fuzzy simple en array de strings
 */
export function fuzzySearchStrings(
  haystack: string[],
  query: string,
  limit = 10
): { item: string; index: number; score: number }[] {
  if (!query || !haystack.length) return [];
  
  const normalizedQuery = normalizeForSearch(query);
  // CVLARGO: misma guarda que FastFuzzy.search
  const uf = buscadorPara(normalizedQuery, new uFuzzy(UFUZZY_OPTIONS), new uFuzzy(UFUZZY_OPTIONS_SIN_ERRATA));
  const normalizedHaystack = haystack.map(normalizeForSearch);
  
  const [idxs, info, order] = uf.search(normalizedHaystack, normalizedQuery);
  
  if (!idxs || idxs.length === 0) return [];
  
  const results: { item: string; index: number; score: number }[] = [];
  const indices = order || idxs;
  
  for (let i = 0; i < Math.min(indices.length, limit); i++) {
    const idx = order ? idxs[order[i]] : indices[i];
    const score = info && order ? (info.idx?.[order[i]] ?? 0) / 100 : i / indices.length * 0.5;
    
    results.push({
      item: haystack[idx],
      index: idx,
      score: Math.min(1, score),
    });
  }
  
  return results;
}

/**
 * Calcula coeficiente de Jaccard entre dos conjuntos de palabras (0-1)
 */
function jaccardSimilarity(wordsA: Set<string>, wordsB: Set<string>): number {
  if (wordsA.size === 0 && wordsB.size === 0) return 1;
  if (wordsA.size === 0 || wordsB.size === 0) return 0;
  
  let intersection = 0;
  for (const word of wordsA) {
    if (wordsB.has(word)) intersection++;
  }
  
  const union = wordsA.size + wordsB.size - intersection;
  return union > 0 ? intersection / union : 0;
}

/**
 * Calcula score de similaridad entre dos strings (0-100)
 * 
 * Estrategia multi-nivel:
 * 1. Match exacto → 100
 * 2. Uno contiene al otro → 70-100
 * 3. Alta similaridad de palabras (Jaccard) → 60-95
 * 4. uFuzzy para match parcial → 0-60
 */
export function calculateSimilarity(a: string, b: string): number {
  if (!a || !b) return 0;
  
  const normalizedA = normalizeForSearch(a);
  const normalizedB = normalizeForSearch(b);
  
  // Match exacto
  if (normalizedA === normalizedB) return 100;
  
  // Uno contiene al otro
  if (normalizedA.includes(normalizedB) || normalizedB.includes(normalizedA)) {
    const ratio = Math.min(normalizedA.length, normalizedB.length) / 
                  Math.max(normalizedA.length, normalizedB.length);
    return Math.round(70 + (ratio * 30));
  }
  
  // Similaridad basada en palabras (Jaccard)
  // Ideal para nombres españoles con "de", "la", "el" variables
  const wordsA = new Set(normalizedA.split(/\s+/).filter(w => w.length > 1));
  const wordsB = new Set(normalizedB.split(/\s+/).filter(w => w.length > 1));
  const jaccard = jaccardSimilarity(wordsA, wordsB);
  
  // Si Jaccard es alto (>0.7), dar score alto sin necesidad de uFuzzy
  // Ej: "centro de salud de quentar" vs "centro de salud quentar" = 4/5 = 0.8 Jaccard
  if (jaccard >= 0.8) {
    return Math.round(85 + (jaccard - 0.8) * 75); // 85-100
  }
  if (jaccard >= 0.6) {
    return Math.round(60 + (jaccard - 0.6) * 125); // 60-85
  }
  
  // uFuzzy para match parcial (casos con baja similaridad de palabras)
  const uf = new uFuzzy(UFUZZY_OPTIONS);
  const [idxs, info, order] = uf.search([normalizedB], normalizedA);
  
  if (!idxs || idxs.length === 0) {
    // Sin match uFuzzy, usar Jaccard si hay algo
    return jaccard > 0 ? Math.round(jaccard * 50) : 0;
  }
  
  // Convertir score uFuzzy a 0-100 y combinar con Jaccard
  if (info && order && order.length > 0) {
    const rawScore = info.idx?.[order[0]] ?? 50;
    const ufuzzyScore = Math.max(0, Math.min(60, 60 - rawScore * 0.6));
    // Combinar: máximo entre uFuzzy y Jaccard escalado
    return Math.max(ufuzzyScore, Math.round(jaccard * 55));
  }
  
  return Math.max(40, Math.round(jaccard * 50)); // Match básico
}

// ============================================================================
// EXPORTS
// ============================================================================

export default FastFuzzy;
